type PermissionAction = "read" | "create" | "edit" | "delete";

interface UserLevelPermissionPayload {
  permissionId: string;
  read?: boolean;
  create?: boolean;
  edit?: boolean;
  delete?: boolean;
}

export const runWithOptionalLoading = async <Result,>(
  showLoading: boolean,
  setLoading: (loading: boolean) => void,
  operation: () => Promise<Result>
): Promise<Result> => {
  if (showLoading) setLoading(true);

  try {
    return await operation();
  } finally {
    if (showLoading) setLoading(false);
  }
};

export const normalizeUserEditId = (value: any): string | null => {
  if (value === null || value === undefined) return null;

  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }

  if (typeof value === "object") {
    const id = value._id ?? value.id ?? value.value ?? value.key;
    return id !== undefined && id !== null ? String(id) : null;
  }

  return null;
};

export const buildUserFormValues = ({
  user,
  rolesList = [],
  permissionsList = [],
  dashboardIds = [],
}: {
  user: any;
  rolesList?: any[];
  permissionsList?: any[];
  dashboardIds?: string[];
}) => {
  const roleObjects = (user?.roles || [])
    .map((role: any) => {
      const id = normalizeUserEditId(role) ?? role;
      return rolesList.find(
        (candidate: any) => String(normalizeUserEditId(candidate) ?? candidate._id ?? candidate.id) === String(id)
      ) || role;
    })
    .filter(Boolean);

  const row = { ...user, roles: roleObjects };
  const initialFormValues: Record<string, any> = {
    firstNameValue: row.firstName,
    lastNameValue: row.lastName,
    emailValue: row.email,
    customerApplicationsValue: (row.customerApplications || [])
      .map((application: any) => normalizeUserEditId(application) ?? application)
      .filter(Boolean),
    rolesValue: roleObjects
      .map((role: any) => String(normalizeUserEditId(role) ?? role?._id ?? role?.id))
      .filter(Boolean),
    dashboardsValue: dashboardIds,
    adminValue: row.admin,
    vendorManagerValue: row.vendorManager,
    isActiveValue: row.isActive,
    targetLanguagesValue: row.targetLanguages || [],
  };

  ["read", "create", "edit", "delete"].forEach((action) => {
    permissionsList.forEach((permission: any) => {
      initialFormValues[`permissions~${action}-${permission.id}`] = false;
    });
  });

  (row.userLevelPermissions || []).forEach((permission: any) => {
    Object.entries(permission).forEach(([key, value]) => {
      if (value === true) {
        initialFormValues[`permissions~${key}-${permission.permissionId}`] = true;
      }
    });
  });

  return initialFormValues;
};

export const buildUserPayloadFromForm = (
  values: Record<string, any>,
  options: { canManagePrivileges?: boolean; originalUser?: any } = {}
) => {
  const userLevelPermissions = new Map<string, UserLevelPermissionPayload>();

  Object.entries(values).forEach(([key, rawValue]) => {
    if (!key.startsWith("permissions~") || !rawValue) return;

    const [action, ...permissionIdParts] = key.slice("permissions~".length).split("-");
    const permissionId = permissionIdParts.join("-");
    if (!["read", "create", "edit", "delete"].includes(action) || !permissionId) return;

    const permission = userLevelPermissions.get(permissionId) || { permissionId };
    permission[action as PermissionAction] = true;
    userLevelPermissions.set(permissionId, permission);
  });

  const payload: Record<string, any> = {
    firstName: values.firstNameValue?.trim(),
    lastName: values.lastNameValue?.trim(),
    email: values.emailValue?.trim(),
    admin: !!values.adminValue,
    vendorManager: !!values.vendorManagerValue,
    isActive: !!values.isActiveValue,
    customerApplications: values.customerApplicationsValue || [],
    roles: values.rolesValue || [],
    dashboards: values.dashboardsValue || [],
    userLevelPermissions: Array.from(userLevelPermissions.values()),
    targetLanguages: values.targetLanguagesValue || [],
  };

  if (options.canManagePrivileges === false) {
    // UMS also rejects these fields server-side for non-admin callers before
    // persistence; see UsersController.updateOne and userUpdatePolicy.spec.ts
    // in the mercury backend repository (work item 830474).
    delete payload.admin;
    delete payload.vendorManager;
    delete payload.userLevelPermissions;
  }

  return payload;
};

export const getDirectDashboardsToPersist = (
  selectedDashboards: any[] = [],
  selectedRoles: any[] = [],
  rolesList: any[] = [],
  baselineDirectDashboards: any[] = []
): string[] => {
  const selected = selectedDashboards
    .map((dashboardId) => normalizeUserEditId(dashboardId))
    .filter(Boolean) as string[];
  const baselineIds = new Set(
    baselineDirectDashboards.map((dashboardId) => normalizeUserEditId(dashboardId)).filter(Boolean)
  );
  const selectedRoleIds = new Set(
    selectedRoles.map((roleId) => normalizeUserEditId(roleId)).filter(Boolean)
  );
  const roleDashboardIds = new Set(
    rolesList
      .filter((role) => selectedRoleIds.has(normalizeUserEditId(role)))
      .flatMap((role) => role?.dashboards || [])
      .map((dashboardId) => normalizeUserEditId(dashboardId))
      .filter(Boolean)
  );

  return selected.filter(
    (dashboardId) => !roleDashboardIds.has(dashboardId) || baselineIds.has(dashboardId)
  );
};